// src/component/MM2026JoinDialog.js
import React, { useContext, useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  Chip,
  Stack,
} from "@mui/material";
import { doc, onSnapshot } from "firebase/firestore";

import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import GraphicEqRoundedIcon from "@mui/icons-material/GraphicEqRounded";
import FavoriteBorderRoundedIcon from "@mui/icons-material/FavoriteBorderRounded";

import PaymentPhases2026 from "./PaymentPhases2026";
import CouponPaymentBox from "./CouponPaymentBox";
import { AppThemeContext } from "./AppShell";
import { auth, db } from "../api/firebase";

export default function MM2026JoinDialog({ open, onClose }) {
  const theme = useContext(AppThemeContext) || {};
  const accent = theme.railActive || "#5A35FF";
  const surface = theme.surfaceBg || "#FFFFFF";
  const textBase = theme.railText || "rgba(30,30,60,0.92)";
  const border = theme.bottomBorder || "rgba(0,0,0,0.08)";

  // 管理者判定
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    const currentUser = auth.currentUser;
    if (!currentUser) return;
    const unsub = onSnapshot(doc(db, "users", currentUser.uid), (snap) => {
      if (snap.exists()) setIsAdmin(!!snap.data().isAdmin);
    });
    return () => unsub();
  }, []);

  // クーポン適用状態（CouponPaymentBox から PaymentPhases2026 に伝搬）
  const [sharedCoupon, setSharedCoupon] = useState(null);
  const [clearCouponSignal, setClearCouponSignal] = useState(0);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: "hidden",
          border: `1px solid ${border}`,
          boxShadow: "0 22px 60px rgba(0,0,0,0.18)",
          bgcolor: surface,
        },
      }}
    >
      {/* タイトル */}
      <DialogTitle
        sx={{
          fontWeight: 900,
          letterSpacing: "0.16em",
          fontSize: 14,
          textTransform: "uppercase",
          color: textBase,
          pb: 1,
          borderBottom: `1px solid ${border}`,
          bgcolor: `${accent}07`,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <AutoAwesomeRoundedIcon
            sx={{ fontSize: 22, color: accent, mt: "-1px" }}
          />
          <Box>
            <Typography
              variant="overline"
              sx={{
                display: "block",
                letterSpacing: "0.26em",
                fontSize: 11,
                opacity: 0.8,
              }}
            >
              MASTER MIND MEMBERS
            </Typography>
            <Typography
              component="div"
              sx={{
                fontSize: 18,
                fontWeight: 800,
                letterSpacing: "0.06em",
              }}
            >
              2026 参加のご案内
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          background:
            "radial-gradient(circle at 0 0, rgba(255,255,255,0.9), transparent 55%)," +
            "radial-gradient(circle at 100% 100%, rgba(255,255,255,0.9), transparent 55%)",
        }}
      >
        {/* ===================== */}
        {/*   メインコピー         */}
        {/* ===================== */}
        <Box
          sx={{
            mb: 4,
            p: { xs: 2, sm: 2.5 },
            borderRadius: 3,
            bgcolor: "rgba(255,255,255,0.96)",
            border: `1px solid ${border}`,
          }}
        >
          <Chip
            icon={<GraphicEqRoundedIcon />}
            label="⚡️マスターマインドメンバーズ2026⚡️"
            size="small"
            sx={{
              mb: 1.5,
              bgcolor: `${accent}10`,
              color: accent,
              fontWeight: 700,
              letterSpacing: "0.04em",
            }}
          />

          {/* 見出し：価格メリット */}
          <Typography
            variant="h6"
            sx={{
              fontWeight: 800,
              mb: 1,
              color: textBase,
            }}
          >
            【今年は約5万円もオトク！】
          </Typography>

          <Typography
            variant="body1"
            sx={{ lineHeight: 1.9, mb: 2, color: textBase }}
          >
            これまでは、1年間11万円の価格でしたが、 2026年は約5万円の大幅な値下げで、
            <Typography component="span" sx={{ fontWeight: 700, color: accent }}>
              1年間5.5万円
            </Typography>
            と、 より多くの人が「参加しやすい」プログラムとなりました！
          </Typography>

          {/* コーヒーの比喩 */}
          <Typography
            variant="body1"
            sx={{ lineHeight: 1.9, mb: 2, color: textBase }}
          >
            「
            <Typography component="span" sx={{ fontWeight: 700, color: accent }}>
              1日1杯のコーヒーより安く
            </Typography>
            」 366回分の「音源」が付いてきて(2026年12月21日まで)、 あなたの毎日をホッと彩ります。
          </Typography>

          {/* 節分スタート〜年間の流れ */}
          <Typography
            variant="body1"
            sx={{ lineHeight: 1.9, mb: 2, color: textBase }}
          >
            節分(ふしわけ)の日から、
            <br />
            1年かけて地球のシューマン共振と同期して行く。
            <br />
            マスターマインドメンバーズ2026。
          </Typography>

          <Typography variant="body1" sx={{ lineHeight: 1.9, color: textBase }}>
            <Typography component="span" sx={{ fontWeight: 700, color: accent }}>
              ＜＜下半期コースがスタート！半額27,500円(税込)でご参加いただけます＞＞
            </Typography>
          </Typography>
        </Box>

        {/* ===================== */}
        {/* クーポン限定決済ブロック */}
        <Divider sx={{ my: 3 }} />
        <CouponPaymentBox
          onCouponChange={setSharedCoupon}
          clearSignal={clearCouponSignal}
        />

        {/* 通常の決済UI（フェーズの adminOnly 設定で表示制御される） */}
        <Divider sx={{ my: 3 }} />
        <PaymentPhases2026
          externalCoupon={sharedCoupon}
          onRemoveExternalCoupon={() => setClearCouponSignal((s) => s + 1)}
        />
      </DialogContent>

      <DialogActions
        sx={{
          borderTop: `1px solid ${border}`,
          bgcolor: "rgba(255,255,255,0.96)",
        }}
      >
        <Button onClick={onClose}>閉じる</Button>
      </DialogActions>
    </Dialog>
  );
}
